import { useState, useEffect, useCallback } from "react";
import QMSLayout from "../components/QMSLayout";
import { useTranslation } from "react-i18next";
import {
  Bell, Mail, MessageCircle, Phone, Video, RefreshCw,
  Filter, ChevronDown, CheckCircle2, XCircle, Clock, Loader2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const channelConfig = {
  email: { icon: Mail, label: "Email", color: "#38BDF8" },
  slack: { icon: MessageCircle, label: "Slack", color: "#4ADE80" },
  teams: { icon: Video, label: "Teams", color: "#A78BFA" },
  sms: { icon: Phone, label: "SMS", color: "#E8A838" },
};

const statusConfig = {
  sent: { icon: CheckCircle2, label: "Sent", color: "#4ADE80" },
  error: { icon: XCircle, label: "Failed", color: "#EF4444" },
  mocked: { icon: Clock, label: "Mocked", color: "#8B95A5" },
};

const NotificationLogPage = () => {
  const { t } = useTranslation('platform');
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterChannel, setFilterChannel] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [page, setPage] = useState(0);
  const limit = 25;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit, skip: page * limit });
      if (filterChannel) params.set("channel", filterChannel);
      if (filterStatus) params.set("status", filterStatus);
      const res = await fetch(`${API}/api/notifications/log?${params}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
        setTotal(data.total || 0);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filterChannel, filterStatus, page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / limit);

  return (
    <QMSLayout title={t('platform:communications.title')}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-['Syne'] text-2xl font-bold text-white" data-testid="notif-log-title">
            Notification Delivery Log
          </h2>
          <p className="text-[#8B95A5] text-sm mt-1">
            Track all notifications sent across channels with delivery status
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[#8B95A5] text-xs">{total} total</span>
          <button onClick={fetchLogs}
            className="p-2 rounded hover:bg-[#1F222D] text-[#8B95A5]" data-testid="refresh-log">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-5">
        <div className="relative">
          <select value={filterChannel} onChange={e => { setFilterChannel(e.target.value); setPage(0); }}
            className="appearance-none bg-[#151820] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2 pr-8 text-white text-xs"
            data-testid="filter-channel">
            <option value="">All Channels</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="slack">Slack</option>
            <option value="teams">Teams</option>
          </select>
          <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#8B95A5] pointer-events-none" />
        </div>
        <div className="relative">
          <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(0); }}
            className="appearance-none bg-[#151820] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2 pr-8 text-white text-xs"
            data-testid="filter-status">
            <option value="">All Statuses</option>
            <option value="sent">Sent</option>
            <option value="error">Failed</option>
            <option value="mocked">Mocked</option>
          </select>
          <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#8B95A5] pointer-events-none" />
        </div>
      </div>

      {/* Log Table */}
      <div className="qms-card" data-testid="notif-log-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[rgba(255,255,255,0.06)]">
                <th className="text-left text-[#8B95A5] text-[10px] uppercase tracking-wider font-medium p-3">Channel</th>
                <th className="text-left text-[#8B95A5] text-[10px] uppercase tracking-wider font-medium p-3">Recipient</th>
                <th className="text-left text-[#8B95A5] text-[10px] uppercase tracking-wider font-medium p-3">Subject</th>
                <th className="text-left text-[#8B95A5] text-[10px] uppercase tracking-wider font-medium p-3">Status</th>
                <th className="text-left text-[#8B95A5] text-[10px] uppercase tracking-wider font-medium p-3">Error</th>
                <th className="text-left text-[#8B95A5] text-[10px] uppercase tracking-wider font-medium p-3">Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {loading && logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-[#8B95A5] text-sm">
                    <Loader2 size={18} className="animate-spin inline mr-2" /> Loading...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-[#8B95A5] text-sm">
                    <Bell size={24} className="inline mr-2 opacity-40" />
                    No notifications logged yet. Send a test notification to see delivery receipts here.
                  </td>
                </tr>
              ) : logs.map((log, i) => {
                const ch = channelConfig[log.channel] || { icon: Bell, label: log.channel, color: "#8B95A5" };
                const st = statusConfig[log.status] || { icon: Clock, label: log.status, color: "#8B95A5" };
                const ChIcon = ch.icon;
                const StIcon = st.icon;
                return (
                  <tr key={i} className="border-b border-[rgba(255,255,255,0.03)] hover:bg-[rgba(255,255,255,0.02)]">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <ChIcon size={14} style={{ color: ch.color }} />
                        <span className="text-white text-xs">{ch.label}</span>
                      </div>
                    </td>
                    <td className="p-3 text-[#CCC] text-xs max-w-[200px] truncate">{log.recipient}</td>
                    <td className="p-3 text-[#CCC] text-xs max-w-[250px] truncate">{log.subject}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1.5">
                        <StIcon size={12} style={{ color: st.color }} />
                        <span className="text-xs" style={{ color: st.color }}>{st.label}</span>
                      </div>
                    </td>
                    <td className="p-3 text-[#EF4444] text-[10px] max-w-[200px] truncate">{log.error || "—"}</td>
                    <td className="p-3 text-[#8B95A5] text-[10px] whitespace-nowrap">
                      {log.timestamp ? new Date(log.timestamp).toLocaleString() : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-[#8B95A5] text-xs">
            Page {page + 1} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
              className="px-3 py-1.5 bg-[#151820] border border-[rgba(255,255,255,0.08)] rounded text-xs text-white disabled:opacity-30"
              data-testid="prev-page">
              Previous
            </button>
            <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}
              className="px-3 py-1.5 bg-[#151820] border border-[rgba(255,255,255,0.08)] rounded text-xs text-white disabled:opacity-30"
              data-testid="next-page">
              Next
            </button>
          </div>
        </div>
      )}
    </QMSLayout>
  );
};

export default NotificationLogPage;
